/**
 * `HolderSection` - the Holder region under Identifiers on the account
 * detail page (SM2-7).
 *
 * Resolves the holder from the account's `legacy_username` via
 * `useHolderQuery` (the primary Murmuration flow) and renders it across three
 * `<Section>` cards plus a distinct Art. 9 vulnerability block:
 *
 *   - Holder   : title, first [masked], last [masked], gender,
 *                date of birth [masked], nationality [masked]
 *   - Contact  : email [masked], mobile [masked], phone [masked],
 *                dialing code, preferred language, notification flags
 *   - Address  : building name [masked], building no [masked],
 *                street [masked], town, postcode [masked], county,
 *                country, ISO country code
 *   - Vulnerability (Art. 9): vulnerable [masked], rating [masked]
 *
 * The masked set is the Highly Confidential `SPA default = masked` set in
 * `01-data-design.md`. Masking reuses the existing `MaskedField`
 * primitive with `resource="holder"` so the reveal emits
 * `murmuration.holder.pii_revealed` (fail-closed, same as accounts). The server
 * returns full values; the client masks; reveal posts an audit event.
 *
 * States:
 *   - loading: a polite status row.
 *   - 404    : "No holder record" empty state (the account exists but no
 *              holder row matches its legacy username).
 *   - error  : inline error with correlation id; no PII in the message.
 */
import type { Account, BffClient, Holder } from '../../../lib/bff-client'
import { BffError } from '../../../lib/bff-client'
import { LoadingPanel } from '../../../components/loading-panel'
import { MaskedField } from '../../../components/masked-field'
import { Field, FieldValue, Section } from './section'
import { useHolderQuery } from './use-holder-query'

export interface HolderSectionProps {
  readonly account: Account
  readonly client: BffClient
  readonly onAuthError?: ((err: unknown) => void) | undefined
}

export function HolderSection({ account, client, onAuthError }: HolderSectionProps): JSX.Element {
  const query = useHolderQuery({ client, legacyUsername: account.legacy_username })

  if (query.isPending || query.isLoading) {
    return (
      <Section title="Holder" columns={1}>
        <div className="col-span-full" data-testid="holder-loading">
          <LoadingPanel label="Loading holder" inline />
        </div>
      </Section>
    )
  }

  if (query.isError) {
    const err = query.error
    const isBffErr = err instanceof BffError
    const status = isBffErr ? err.status : 0
    if (status === 404) {
      return (
        <Section title="Holder" columns={1}>
          <div className="col-span-full text-text-muted" data-testid="holder-empty">
            No holder record for this account.
          </div>
        </Section>
      )
    }
    const correlationId = isBffErr ? err.correlationId : null
    return (
      <Section title="Holder" columns={1}>
        <div role="alert" className="col-span-full text-status-danger" data-testid="holder-error">
          Could not load the holder record.
          {correlationId !== null && correlationId !== '' && (
            <>
              {' '}
              Correlation id: <code className="select-all font-mono">{correlationId}</code>
            </>
          )}
        </div>
      </Section>
    )
  }

  const holder = query.data
  return <HolderDetail holder={holder} client={client} onAuthError={onAuthError} />
}

function HolderDetail({
  holder,
  client,
  onAuthError,
}: {
  readonly holder: Holder
  readonly client: BffClient
  readonly onAuthError?: ((err: unknown) => void) | undefined
}): JSX.Element {
  // The holder UUID is the audit reference for every holder reveal.
  const holderId = holder.id
  const masked = (
    field: Parameters<typeof MaskedField>[0]['field'],
    value: string | null,
    label: string,
  ): JSX.Element => (
    <MaskedField
      client={client}
      accountId={holderId}
      resource="holder"
      field={field}
      value={value}
      label={label}
      onAuthError={onAuthError}
    />
  )

  return (
    <>
      {/*
        The audit warning, on screen. Each masked button already carries it
        in its accessible name, so screen-reader users were told and sighted
        users were not - and the people clicking these are exactly who the
        deterrent is for.
      */}
      <Section
        title="Holder"
        columns={1}
        footnote="Click a hidden value to reveal it. Every reveal is recorded against your staff identity."
      >
        <Field label="Title">
          <FieldValue value={holder.title} />
        </Field>
        <Field label="First name">{masked('first_name', holder.first_name, 'first name')}</Field>
        <Field label="Last name">{masked('last_name', holder.last_name, 'last name')}</Field>
        <Field label="Gender">
          <FieldValue value={holder.gender} />
        </Field>
        <Field label="Date of birth">
          {masked('date_of_birth', holder.date_of_birth, 'date of birth')}
        </Field>
        <Field label="Nationality">
          {masked('nationality', holder.nationality, 'nationality')}
        </Field>
      </Section>

      <Section title="Contact" columns={1}>
        <Field label="Email">{masked('email_address', holder.email_address, 'email')}</Field>
        <Field label="Mobile">{masked('mobile_no', holder.mobile_no, 'mobile number')}</Field>
        <Field label="Phone">
          {masked('full_phone_number', holder.full_phone_number, 'phone number')}
        </Field>
        <Field label="Dialing code">
          <FieldValue value={holder.dialing_code} />
        </Field>
        <Field label="Preferred language">
          <FieldValue value={holder.preferred_language} />
        </Field>
        <Field label="Email notifications">
          <BoolValue value={holder.email_notification} />
        </Field>
        <Field label="Push notifications">
          <BoolValue value={holder.push_notification} />
        </Field>
        <Field label="SMS notifications">
          <BoolValue value={holder.sms_notification} />
        </Field>
      </Section>

      <Section title="Address" columns={1}>
        <Field label="Building name">
          {masked('building_name', holder.building_name, 'building name')}
        </Field>
        <Field label="Building number">
          {masked('building_no', holder.building_no, 'building number')}
        </Field>
        <Field label="Street">{masked('street', holder.street, 'street')}</Field>
        <Field label="Town">
          <FieldValue value={holder.town} />
        </Field>
        <Field label="Postcode">{masked('postcode', holder.postcode, 'postcode')}</Field>
        <Field label="County">
          <FieldValue value={holder.county} />
        </Field>
        <Field label="Country">
          <FieldValue value={holder.country} />
        </Field>
        <Field label="ISO country code">
          <FieldValue value={holder.iso_country_code} />
        </Field>
      </Section>

      {/*
        Vulnerability is GDPR Art. 9 special-category data. It is rendered
        in a distinct, clearly-labelled block so a staff member can never
        confuse it with ordinary contact data.

        TODO(DAQ-H-G-1): gate behind bo.holder:read-vulnerability once the
        DPO ratifies the dedicated role. For now the reveal uses the same
        gating as all other holder PII (the shared MaskedField + the
        BFF-side fail-closed audit); the dedicated role is an open question
        and is NOT invented as enforced here.
      */}
      <Section title="Vulnerability (special category - GDPR Art. 9)" columns={1}>
        <Field label="Vulnerable">
          {masked('vulnerable', boolToText(holder.vulnerable), 'vulnerability flag')}
        </Field>
        <Field label="Vulnerability rating">
          {masked('vulnerability_rating', holder.vulnerability_rating, 'vulnerability rating')}
        </Field>
      </Section>
    </>
  )
}

/** Render a visible boolean as Yes / No / n/a. */
function BoolValue({ value }: { readonly value: boolean | null }): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-text-muted">n/a</span>
  }
  return <span>{value ? 'Yes' : 'No'}</span>
}

/**
 * Convert the Art. 9 boolean `vulnerable` flag to the string MaskedField
 * masks and reveals. Null stays null so the field renders `n/a` rather
 * than masking an absent value.
 */
function boolToText(value: boolean | null): string | null {
  if (value === null || value === undefined) return null
  return value ? 'Yes' : 'No'
}
